import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Payment {
  date: string
  employee_id: number
  employee_name: string
  amount: number
}

interface Employee {
  id: number
  name: string
}

interface Props {
  payments: Payment[]
  start_date: string
  end_date: string
  employees: Employee[]
  selected_employee_id: number | null
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 20

const columns: HpiColumn<Payment>[] = [
  {
    header: "Date",
    width: "max-w-[140px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Employee",
    render: (row) => <div className="text-sm">{row.employee_name}</div>,
  },
  {
    header: "Amount",
    width: "max-w-[140px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Expense History", href: "/accounting/expenses/history" },
]

export default function ExpenseHistory({
  payments,
  start_date,
  end_date,
  employees,
  selected_employee_id,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [employeeId, setEmployeeId] = useState<string>(
    selected_employee_id?.toString() ?? "",
  )
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(payments.length / PER_PAGE)
  const paginated = payments.slice((page - 1) * PER_PAGE, page * PER_PAGE)
  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0)

  const handleUpdate = () => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    router.get("/accounting/expenses/history", params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Expense History" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            options={employees.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="All Employees"
          />
        </HpiDateFilterBar>

        <HpiDataTable<Payment>
          title={`Expense Payments (${payments.length} payments — ${formatCurrency(totalAmount)})`}
          columns={columns}
          data={paginated}
          keyFn={(row) => `${row.date}-${row.employee_id}`}
          rowHref={(row) =>
            `/accounting/expenses/history?employee_id=${row.employee_id}&date=${row.date}&start_date=${startDate}&end_date=${endDate}`
          }
          pagination={
            payments.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={payments.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />
      </div>
    </AppLayout>
  )
}
